import { createApi } from '@reduxjs/toolkit/query/react';
import { ChannelType, HttpMethod } from '@vision/ui/enums';
import {
  ApiBaseRequest,
  ApiResponse,
  HttpBaseRequest,
  INode,
  SimpleChannel,
  SimpleNode,
  User,
} from '@vision/ui/interfaces';
import { buildEndpointWithQuery, insertIfObject } from '@vision/ui/utils';
import { axiosBaseQuery } from './base.service';

export const NodesService = createApi({
  reducerPath: 'NodesService',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getNodeChildren: builder.query<
      INode[],
      HttpBaseRequest<{ nodeId: string; lightweight?: boolean; includeSelf?: boolean; type?: string }>
    >({
      query: ({ nodeId, lightweight, includeSelf, type }) => ({
        url: `/v1/nodes/${nodeId}/children`,
        method: HttpMethod.GET,
        params: {
          lightweight,
          include_self: includeSelf,
          type,
        },
      }),
      keepUnusedDataFor: 30,
    }),
    nodeUsers: builder.query<User[], { nodeId: string }>({
      query: ({ nodeId }) => ({
        url: `/v1/nodes/${nodeId}/users`,
        method: HttpMethod.GET,
      }),
    }),
    nodeDetail: builder.query<INode, { nodeId: string }>({
      query: ({ nodeId }) => ({
        url: `/v1/nodes/${nodeId}/detail`,
        method: HttpMethod.GET,
      }),
    }),
    getNode: builder.query<INode, { nodeId: string }>({
      query: ({ nodeId }) => ({
        url: `/v1/nodes/${nodeId}`,
        method: HttpMethod.GET,
      }),
    }),
    fetchChannelsWithPagination: builder.query<
      ApiResponse<SimpleChannel[]>,
      ApiBaseRequest & { types?: ChannelType[] }
    >({
      query: ({ nodeId, query, types, sortBy, sortOrder }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/dashboards/nodes`, query),
        method: HttpMethod.GET,
        params: {
          ...insertIfObject(!!types && !!types.length, {
            types: types.join(','),
          }),
          ...insertIfObject(!!sortBy, {
            sort_by: sortBy,
          }),
          ...insertIfObject(!!sortOrder, {
            sort_order: sortOrder,
          }),
        },
      }),
    }),
    getNodeTypes: builder.query<string[], { nodeId: string; types: string[] }>({
      query: ({ nodeId, types }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/node_types`,
        method: HttpMethod.GET,
        params: {
          ...insertIfObject(!!types && !!types.length, {
            types: types?.join(','),
          }),
        },
      }),
    }),
    getChildrenCountList: builder.query<Record<ChannelType, number>, { nodeId: string }>({
      query: ({ nodeId }) => ({
        url: `/v1/nodes/${nodeId}/children_count_list`,
        method: HttpMethod.GET,
      }),
    }),
    fetchNodesWithPagination: builder.query<
      ApiResponse<SimpleNode[]>,
      ApiBaseRequest & { types: string[]; includeSelf?: boolean }
    >({
      query: ({ nodeId, query, types, includeSelf = false, sortBy, sortOrder }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/dashboards/nodes`, query),
        method: HttpMethod.GET,
        params: {
          ...insertIfObject(!!types && !!types.length, {
            types: types?.join(','),
          }),
          ...insertIfObject(!!includeSelf, {
            include_self: includeSelf,
          }),
          ...insertIfObject(!!sortBy, {
            sort_by: sortBy,
          }),
          ...insertIfObject(!!sortOrder, {
            sort_order: sortOrder,
          }),
        },
      }),
    }),
  }),
});

export const {
  useGetNodeChildrenQuery,
  useGetNodeQuery,
  useNodeUsersQuery,
  useNodeDetailQuery,
  useGetNodeTypesQuery,
  useGetChildrenCountListQuery,
  useLazyGetChildrenCountListQuery,
  useLazyFetchChannelsWithPaginationQuery,
  useLazyFetchNodesWithPaginationQuery,
} = NodesService;
